import { Component } from 'react';
import { API_BASE } from 'config/api';

export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://www.statselite.co.tz').replace(/\/+$/, '');
export const SITE_NAME = 'Stats Elite';
export const DEFAULT_TITLE = 'Stats Elite | Data Consulting, MERL and Analytics Training';
export const DEFAULT_DESCRIPTION = 'Stats Elite helps organizations turn data into evidence-based decisions through MERL, statistics, analytics consulting, research support, and professional training.';
export const DEFAULT_IMAGE = `${SITE_URL}/images/slides/stats-elite-data-consulting.png`;

const upsertMeta = (selector, attributes) => {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement('meta');
        document.head.appendChild(element);
    }
    Object.entries(attributes).forEach(([key, value]) => {
        if (value === null || typeof value === 'undefined') {
            element.removeAttribute(key);
            return;
        }
        element.setAttribute(key, value);
    });
};

const upsertLink = (rel, href) => {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
};

const upsertJsonLd = (id, data) => {
    let element = document.getElementById(id);
    if (!data) {
        if (element) element.remove();
        return;
    }
    if (!element) {
        element = document.createElement('script');
        element.id = id;
        element.type = 'application/ld+json';
        document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(data);
};

export const stripHtml = (value = '') => value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const truncateDescription = (value = '') => {
    const clean = stripHtml(value);
    return clean.length > 158 ? `${clean.slice(0, 155).trim()}...` : clean;
};

export const buildAbsoluteUrl = (path = '/') => {
    if (/^https?:\/\//i.test(path)) return path;
    return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo@2x.png`,
    image: DEFAULT_IMAGE,
    email: 'info@statselite.co.tz',
    telephone: '+255 749 663 117',
    address: {
        '@type': 'PostalAddress',
        addressLocality: 'Dar es Salaam',
        addressCountry: 'TZ'
    },
    areaServed: ['Tanzania', 'East Africa'],
    serviceType: [
        'MERL consulting',
        'Statistics training',
        'Data analytics consulting',
        'Research and evaluation support'
    ]
};

let siteSettingsCache = null;
let siteSettingsRequest = null;

const fetchSiteSettings = async () => {
    if (siteSettingsCache) return siteSettingsCache;
    if (!siteSettingsRequest) {
        siteSettingsRequest = fetch(`${API_BASE}/settings`)
            .then((response) => response.ok ? response.json() : null)
            .then((settings) => {
                siteSettingsCache = settings;
                return settings;
            })
            .catch(() => null);
    }
    return siteSettingsRequest;
};

class SEO extends Component {
    state = {
        settings: null
    };

    componentDidMount() {
        this.applyMetadata();
        fetchSiteSettings().then((settings) => {
            if (settings) {
                this.setState({ settings }, this.applyMetadata);
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps) !== JSON.stringify(this.props)) {
            this.applyMetadata();
        }
    }

    getSettingsMetadata() {
        const { settings } = this.state;
        const { pageKey } = this.props;
        const globalSeo = settings?.seo || {};
        const pageSeo = pageKey ? globalSeo.pages?.[pageKey] || {} : {};

        return {
            siteName: settings?.siteName || SITE_NAME,
            title: pageSeo.metaTitle || (pageKey === 'home' ? globalSeo.metaTitle : ''),
            description: pageSeo.metaDescription || (pageKey === 'home' ? globalSeo.metaDescription : ''),
            keywords: pageSeo.keywords || globalSeo.keywords,
            image: globalSeo.ogImage || ''
        };
    }

    applyMetadata = () => {
        if (typeof document === 'undefined') return;

        const {
            title = DEFAULT_TITLE,
            description = DEFAULT_DESCRIPTION,
            path,
            canonical,
            image = DEFAULT_IMAGE,
            type = 'website',
            robots = 'index,follow',
            schema
        } = this.props;
        const settingsMetadata = this.getSettingsMetadata();

        const canonicalUrl = canonical || buildAbsoluteUrl(path || window.location.pathname || '/');
        const resolvedTitle = settingsMetadata.title || title;
        const resolvedDescription = settingsMetadata.description || description;
        const resolvedImage = settingsMetadata.image || image;
        const cleanDescription = truncateDescription(resolvedDescription) || DEFAULT_DESCRIPTION;

        document.title = resolvedTitle;
        upsertMeta('meta[name="description"]', { name: 'description', content: cleanDescription });
        upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
        if (settingsMetadata.keywords) {
            const keywords = Array.isArray(settingsMetadata.keywords) ? settingsMetadata.keywords.join(', ') : settingsMetadata.keywords;
            upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
        }
        upsertLink('canonical', canonicalUrl);

        upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: settingsMetadata.siteName });
        upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
        upsertMeta('meta[property="og:title"]', { property: 'og:title', content: resolvedTitle });
        upsertMeta('meta[property="og:description"]', { property: 'og:description', content: cleanDescription });
        upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
        upsertMeta('meta[property="og:image"]', { property: 'og:image', content: resolvedImage });

        upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
        upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: resolvedTitle });
        upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: cleanDescription });
        upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: resolvedImage });

        upsertJsonLd('stats-elite-organization-schema', organizationSchema);
        upsertJsonLd('stats-elite-page-schema', schema);
    };

    render() {
        return null;
    }
}

export default SEO;
